"use client";

import { useState, useEffect, useCallback } from "react";

export type RequestModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function RequestModal({ open, onClose }: RequestModalProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    setDone(false);
    setError(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError("Заполните имя и телефон");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, phone, comment, source: "company-landing" }),
      });
      if (!res.ok) throw new Error("send failed");
      setDone(true);
    } catch {
      setError("Не удалось отправить. Попробуйте позже или позвоните.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      onClick={close}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] rounded-[24px] bg-white p-8"
      >
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-2xl font-semibold tracking-tight">
            Получить предложение
          </h3>
          <button
            onClick={close}
            aria-label="Закрыть"
            className="text-2xl leading-none text-black/50 hover:text-black"
          >
            ×
          </button>
        </div>
        {done ? (
          <div className="py-6 text-center">
            <p className="mb-2 text-lg font-medium">Заявка отправлена</p>
            <p className="text-sm text-black/60">
              Мы свяжемся с вами в ближайшее время.
            </p>
            <button
              onClick={close}
              className="mt-4 rounded-full bg-primary px-6 py-3 text-white"
            >
              Закрыть
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-3">
            <p className="text-sm text-black/60">
              Оставьте контакты — менеджер позвонит и расскажет про условия.
            </p>
            <input
              type="text"
              placeholder="Имя"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="rounded-xl border border-black/15 px-4 py-3 outline-none focus:border-primary"
            />
            <input
              type="tel"
              placeholder="Телефон"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={32}
              className="rounded-xl border border-black/15 px-4 py-3 outline-none focus:border-primary"
            />
            <textarea
              placeholder="Комментарий (необязательно)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={3}
              className="resize-none rounded-xl border border-black/15 px-4 py-3 outline-none focus:border-primary"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={sending}
              className="rounded-full bg-primary py-3 font-medium text-white disabled:opacity-60"
            >
              {sending ? "Отправляем..." : "Отправить заявку"}
            </button>
            <p className="text-xs text-black/40">
              Нажимая на кнопку, вы соглашаетесь с обработкой персональных
              данных.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
